import { cache } from '../cache'

export type SupportedCurrencies = keyof typeof currencyDetails

export type PriceTable = {
    [key in SupportedCurrencies]: {
        locale: string
        shortName?: string
        symbol: string
        shortSymbol: string
        unlimited: { monthly: number; yearly: number }
    }
}
export const currencyDetails = {
    USD: {
        locale: 'en-US',
        symbol: 'US$',
        shortName: 'US',
        shortSymbol: '$',
    },
    EUR: {
        locale: 'en',
        symbol: '€',
        shortSymbol: '€',
    },
    GBP: {
        locale: 'en-GB',
        symbol: '£',
        shortSymbol: '£',
    },
    DKK: {
        locale: 'da-DK',
        symbol: 'kr',
        shortSymbol: 'kr',
    },
    NOK: {
        locale: 'no-NO',
        symbol: 'kr',
        shortSymbol: 'kr',
    },
    BRL: {
        locale: 'pt-BR',
        symbol: 'R$',
        shortSymbol: 'R$',
    },
    SEK: {
        locale: 'sv-SE',
        symbol: 'kr',
        shortSymbol: 'kr',
    },
    JPY: {
        locale: 'jp-JP',
        symbol: '¥',
        shortSymbol: '¥',
    },
    CAD: {
        locale: 'en-CA',
        shortName: 'C',
        symbol: 'C$',
        shortSymbol: '$',
    },
    AUD: {
        locale: 'en-AU',
        shortName: 'A',
        symbol: 'A$',
        shortSymbol: '$',
    },
}

export async function getPricingModel(): Promise<PriceTable> {
    const cached = cache.get(cache.keys.PRICING_MODEL)
    if (cached) {
        return JSON.parse(cached)
    }

    /** Fetch from backend. */
    const abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController.abort(), 10000)
    const res = await fetch(`${process.env.API_BASE_URL}/api/v3/pricing`, {
        signal: abortController.signal,
    })
    clearTimeout(timeoutId)
    const model = await res.json()
    const prices = model[model.latest_unlimited].unlimited
    const priceTable: Partial<PriceTable> = {}
    for (const currency in currencyDetails) {
        const details = currencyDetails[currency as SupportedCurrencies]
        priceTable[currency as SupportedCurrencies] = {
            ...details,
            unlimited: prices[currency.toLowerCase()],
        }
    }
    cache.set(cache.keys.PRICING_MODEL, JSON.stringify(priceTable))
    return priceTable as PriceTable
}

export function isSupportedCurrency(currency: string) {
    return (Object.keys(currencyDetails) as SupportedCurrencies[] as string[]).includes(currency)
}

export function formatPrice(amountInCents: number, locale: string, currency: SupportedCurrencies) {
    const coef = currency === 'JPY' ? 1 : 100
    return new Intl.NumberFormat(locale).format(amountInCents / coef)
}
